import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import {
  INN_ERROR,
  KNM_SUBJECT_IS_EXIST_AT_ACT_ERROR,
  MAX_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { KnmResponsibleTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { IKnmPunishmentNature } from 'APIPackage/KnmModule/interfaces/IKnmPunishmentNature';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { ICatalog } from 'CorePackage/CatalogsModule/interfaces/ICatalog';
import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { IKnmFormDocumentCommon } from 'KnmPackage/MainModule/interfaces/IKnmFormDocumentCommon';
import { getKnmSubjectValidate } from 'KnmPackage/KnmSubjectModule/common/getKnmSubjectValidate';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { KnmResultItemSubjectTypesEnum } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultItemSubjectInfo';

import { KnmSubjectTypesEnum } from '../../ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmErknmFormOrganizationAdd } from '../interfaces/IKnmErknmFormOrganizationAdd';
import { ResponsibilityTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/ResponsibilityTypeCodesEnum';
import { KindResultDecisionCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { getErknmResponsibleEntitiesOrganizationValidate } from './getErknmResponsibleEntitiesOrganizationValidate';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { getSignersValidator } from './getSignersValidator';
import { getFamiliarizationNotificationsValidate } from './getFamiliarizationNotificationsValidate';
import {
  getProductSalesResumptionValidate,
  getProductSalesValidToValidate,
} from './getErknmProductSalesSuspensionValidate';

interface IGetErknmResultDecisionsValidateProps {
  resultDecisions: IKnmErknmFormResultDecision[] | undefined;
  getOrganizationValidate: (
    item: IKnmErknmFormSubject | IKnmErknmFormOrganizationAdd,
    errorMsg: string | undefined,
    orgs: IKnmErknmFormSubject[] | IKnmErknmFormOrganizationAdd[] | undefined,
  ) => FormErrors<IKnmErknmFormSubject>;
  punishmentNature: IKnmPunishmentNature[] | undefined;
  isShow4127: boolean;
  kindKnm: IDictionaryKnmKindSelectOption | undefined;
  isKnmStatusFillInProcess: boolean | undefined;
  isIntegrationCategoryValidate: boolean;
  isValidateRequiredViolatedMandatory: boolean;
  isViolationsStartDate: boolean;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
  eventsErknm: IKnmErknmFormEvent[] | undefined;
  acts: IKnmErknmFormAct[] | undefined;
  organizations: IKnmErknmFormSubject[] | undefined;
  fnsRegistryOkvedCodes:
    | {
        [key: string]: ICatalog<string[]>;
      }
    | undefined;
  isShowCheckResultMessage: boolean;
  isKnmStatusTypeCarriedOut: boolean | undefined;
  isSupervisor: boolean | undefined;
  isNewKnm: boolean | undefined;
  isKnmStatusReadyForApprove: boolean | undefined;
  isKnmPlanStatusConsidered: boolean;
  knmDataApprovedType: ApprovedTypesEnum | null | undefined;
  knmDataNote: string | null | undefined;
  isWatchOnly: boolean | undefined;
  isLater4111ForDecision: boolean;
  isUseSigners: boolean;
}

export const getErknmResultDecisionsValidate = ({
  resultDecisions,
  getOrganizationValidate,
  punishmentNature,
  isShow4127,
  kindKnm,
  isKnmStatusFillInProcess,
  isIntegrationCategoryValidate,
  isValidateRequiredViolatedMandatory,
  isViolationsStartDate,
  subjectTypeOptions,
  eventsErknm,
  acts,
  organizations,
  fnsRegistryOkvedCodes,
  isKnmStatusTypeCarriedOut,
  isShowCheckResultMessage,
  isKnmPlanStatusConsidered,
  isKnmStatusReadyForApprove,
  isNewKnm,
  isSupervisor,
  knmDataApprovedType,
  knmDataNote,
  isWatchOnly,
  isLater4111ForDecision,
  isUseSigners,
}: IGetErknmResultDecisionsValidateProps) => {
  const resultDecisionOrganizations: IKnmErknmFormOrganizationAdd[] | undefined = [];

  resultDecisions?.forEach(i => {
    if (i.organization) {
      resultDecisionOrganizations?.push(i.organization);
    }
  });

  return resultDecisions?.map(item => {
    const isOrganizationFiz = item.organization?.subjectType?.label === KnmSubjectTypesEnum.FIZ;
    const isOrganizationNotRegisteredIur =
      item.organization?.subjectType?.label === KnmSubjectTypesEnum.IUR &&
      item.organization.isForeignSubjectAgencyNotRegistredInRf;

    let organizationValidate;

    if (item.organization) {
      const defaultOrgValidate = getOrganizationValidate(
        item.organization,
        KNM_SUBJECT_IS_EXIST_AT_ACT_ERROR,
        resultDecisionOrganizations,
      );

      organizationValidate = {
        ...defaultOrgValidate,
        country: isOrganizationNotRegisteredIur
          ? makeRequired('Поле не может быть пустым')(item.organization.country)
          : undefined,
      };

      if (isOrganizationFiz || isOrganizationNotRegisteredIur) {
        organizationValidate.organizationName = makeRequired('Поле не может быть пустым')(
          item.organization.organizationName,
        );
      }
    }

    const shouldValidatePunishmentNature =
      item.isProsecution?.value &&
      item.kindDecision &&
      !!punishmentNature?.filter(i => i.kindResultDecisionDTO.code === item.kindDecision?.code).length;

    const isRequiredFileProtocol = shouldValidatePunishmentNature && item.punishmentNature?.id.toString() === '1';

    const isPrescription =
      item.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
      item.kindDecision?.code === KindResultDecisionCodesEnum.D_1;

    /**
     * Валидируем "Лицо, в отношении которого принято решение" только
     * в предписании безбумажного решения, в соответствии с составом формы
     */
    const isValidateSubjectTypeFounder =
      item.paperless &&
      (isPrescription || item.kindDecision?.code === KindResultDecisionCodesEnum.D_10) &&
      item.organization?.subjectType?.code &&
      isLater4111ForDecision &&
      item.organization.subjectType.code === KnmResultItemSubjectTypesEnum.UR;

    return {
      noteLawsuits: item.noteLawsuits?.map(noteLawsuit => ({
        // value: makeRequired('Поле не может быть пустым')(noteLawsuit.value),
        valueReactQuillEditor: makeRequiredQuillEditorDelta('Поле не может быть пустым')(
          noteLawsuit.valueReactQuillEditor,
        ),
      })),
      kindDecision: makeRequired('Поле не может быть пустым')(item.kindDecision),
      numberDecision:
        (!isPrescription && !item.paperless) || item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? makeRequired('Поле не может быть пустым')(item.numberDecision)
          : undefined,
      dateDecision:
        (!isPrescription && !item.paperless) || item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? makeRequired('Поле не может быть пустым')(item.dateDecision)
          : undefined,
      punishmentNature: shouldValidatePunishmentNature
        ? makeRequired('Поле не может быть пустым')(item.punishmentNature)
        : undefined,
      founder:
        item.subjectTypeFounder?.id === KnmResultDecisionSubjectTypesEnum.FOUNDER
          ? getKnmSubjectValidate(item.founder)
          : undefined,
      inspectors:
        // eslint-disable-next-line no-nested-ternary
        item.paperless || !isShow4127
          ? undefined
          : !item.inspectors || item.inspectors?.length === 0
          ? 'Поле не может быть пустым'
          : item.inspectors?.map(inspector => {
              // @ts-ignore
              if (!inspector) {
                return { 0: 'Поле не может быть пустым' };
              }

              // @ts-ignore
              if (inspector.value) {
                return {};
              }

              return {
                fullName: makeRequired('Поле не может быть пустым')(inspector.fullName),
                position: makeRequired('Поле не может быть пустым')(inspector.position),
              };
            }),
      protocol: isRequiredFileProtocol ? documentCommonRequired(item.protocol) : undefined,
      organizationTemplate:
        kindKnm?.code === KnmKindCodesEnum.RO ? makeRequired(REQUIRED_ERROR)(item.organizationTemplate) : undefined,
      subjectTypeFounder: isValidateSubjectTypeFounder
        ? makeRequired('Поле не может быть пустым')(item.subjectTypeFounder)
        : undefined,
      organization: item.organization
        ? {
            ...organizationValidate,
            country: isOrganizationNotRegisteredIur
              ? makeRequired('Поле не может быть пустым')(item.organization.country)
              : undefined,
          }
        : undefined,
      isProsecution:
        !isPrescription && !isKnmStatusTypeCarriedOut && item.kindDecision?.code !== KindResultDecisionCodesEnum.D_10
          ? makeRequired('Поле не может быть пустым')(item.isProsecution)
          : undefined,
      responsibleEntities:
        item.isProsecution?.value && (!item.responsibleEntities || item.responsibleEntities.length === 0)
          ? 'Поле не может быть пустым'
          : item.responsibleEntities?.map(responsibleEntity => {
              const responsibilityTypeCode = responsibleEntity.responsibilityType?.code;
              const isRspTypeI = responsibilityTypeCode === ResponsibilityTypeCodesEnum.RSP_TYPE_I;
              const isRspTypeII = responsibilityTypeCode === ResponsibilityTypeCodesEnum.RSP_TYPE_II;
              const isRspTypeIII = responsibilityTypeCode === ResponsibilityTypeCodesEnum.RSP_TYPE_III;

              return {
                subjectType: isShow4127
                  ? undefined
                  : makeRequired('Поле не может быть пустым')(responsibleEntity.subjectType),
                inn:
                  isShow4127 || responsibleEntity.subjectType?.code !== KnmResponsibleTypeCodesEnum.ULIP
                    ? undefined
                    : composeValidators(
                        makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
                        makeIsErknmInn({ errorMessage: INN_ERROR(true) }),
                        makeRequired('Поле не может быть пустым'),
                      )(responsibleEntity.inn),
                ogrn:
                  isShow4127 || responsibleEntity.subjectType?.code !== KnmResponsibleTypeCodesEnum.ULIP
                    ? undefined
                    : composeValidators(
                        makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
                        makeMaxCharactersValidator(32, MAX_CHARACTERS_ERROR),
                        makeIsOgrn(OGRN_LENGTH_ERROR),
                        makeRequired('Поле не может быть пустым'),
                      )(responsibleEntity.ogrn),
                organizationName:
                  isShow4127 || responsibleEntity.subjectType?.code !== KnmResponsibleTypeCodesEnum.ULIP
                    ? undefined
                    : makeRequired('Поле не может быть пустым')(responsibleEntity.organizationName),
                fullName:
                  isShow4127 || responsibleEntity.subjectType?.code === KnmResponsibleTypeCodesEnum.ULIP
                    ? undefined
                    : makeRequired('Поле не может быть пустым', () => false)(responsibleEntity.fullName),
                snils:
                  !isShow4127 &&
                  (responsibleEntity.snils || !responsibleEntity.fullName) &&
                  responsibleEntity.subjectType?.code !== KnmResponsibleTypeCodesEnum.ULIP &&
                  !isKnmStatusFillInProcess
                    ? composeValidators(
                        makeRequired('Поле не может быть пустым'),
                        makeSnilsValidation(UNALLOWED_CHARACTERS_ERROR),
                      )(responsibleEntity.snils)
                    : undefined,
                position:
                  isShow4127 || responsibleEntity.subjectType?.code !== KnmResponsibleTypeCodesEnum.OFFICIAL
                    ? undefined
                    : makeRequired('Поле не может быть пустым')(responsibleEntity.position),

                // eslint-disable-next-line no-nested-ternary
                organization: !isShow4127
                  ? undefined
                  : responsibleEntity.organization
                  ? {
                      value: responsibleEntity.organization?.value
                        ? getErknmResponsibleEntitiesOrganizationValidate({
                            item: responsibleEntity.organization.value,
                            isOther: !!responsibleEntity.organization.isOther,
                            subjectTypeOptions,
                            eventsErknm,
                            acts,
                            organizations,
                            fnsRegistryOkvedCodes,
                            isKnmStatusFillInProcess,
                            isShowCheckResultMessage,
                            kindKnm,
                            isKnmPlanStatusConsidered,
                            isKnmStatusReadyForApprove,
                            isNewKnm,
                            isSupervisor,
                            knmDataApprovedType,
                            knmDataNote,
                            isWatchOnly,
                          })
                        : 'Поле не может быть пустым',
                    }
                  : 'Поле не может быть пустым',
                responsibilityType: makeRequired('Поле не может быть пустым')(responsibleEntity.responsibilityType),
                punishmentAmount:
                  isRspTypeI && !isKnmStatusFillInProcess
                    ? makeRequired('Поле не может быть пустым')(responsibleEntity.punishmentAmount)
                    : undefined,
                punishmentAmountMeasure: isRspTypeI
                  ? makeRequired('Поле не может быть пустым')(responsibleEntity.punishmentAmountMeasure)
                  : undefined,
                punishmentTerm:
                  isRspTypeII || isRspTypeIII
                    ? makeRequired('Поле не может быть пустым')(responsibleEntity.punishmentTerm)
                    : undefined,
                ervkControlKindNpaUnit: undefined,
                structuresNPA:
                  !responsibleEntity.structuresNPA || !responsibleEntity.structuresNPA?.length
                    ? makeRequired('Поле не может быть пустым')(responsibleEntity.structuresNPA)
                    : responsibleEntity.structuresNPA?.map(structureNPA => ({
                        value: makeRequired('Поле не может быть пустым')(structureNPA.value),
                      })),
              };
            }),
      violatedMandatoryRequirements:
        (isIntegrationCategoryValidate && isPrescription) ||
        item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? composeValidators(
              makeRequiredArray('Поле не может быть пустым'),
              getViolatedMandatoryRequirementsValidator({
                isSigned: item.paperless ? item.signed : undefined,
                isValidateRequiredViolatedMandatory,
                isDecisionMode: true,
                isProdReady: isViolationsStartDate,
                isPaperless: item.paperless,
                isNeedViolationPLanFixValidate: item.kindDecision?.code !== KindResultDecisionCodesEnum.D_10,
                isDecision: false,
                isActMode: false,
                isViolationEventsRequired: false,
              }),
            )(item.violatedMandatoryRequirements)
          : undefined,
      act:
        !item.paperless || (!isPrescription && item.kindDecision?.code !== KindResultDecisionCodesEnum.D_10)
          ? undefined
          : makeRequired('Поле не может быть пустым')(item.act),
      fioSigner:
        !item.paperless ||
        item.systemSignatureUsed ||
        (isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1))
          ? undefined
          : makeRequired('Поле не может быть пустым')(item.fioSigner),
      titleSigner:
        !item.paperless ||
        item.systemSignatureUsed ||
        (isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1))
          ? undefined
          : makeRequired('Поле не может быть пустым')(item.titleSigner),
      signers:
        isUseSigners &&
        (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
          item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1)
          ? getSignersValidator(item.signers)
          : undefined,
      injunction: isPrescription
        ? {
            // note: !item?.violatedMandatoryRequirements?.length
            //   ? makeRequired('Поле не может быть пустым')(item?.injunction?.note)
            //   : undefined,
            noteReactQuillEditor: !item?.violatedMandatoryRequirements?.length
              ? makeRequiredQuillEditorDelta('Поле не может быть пустым')(item?.injunction?.noteReactQuillEditor)
              : undefined,
            dateResolved:
              item.paperless || isKnmStatusTypeCarriedOut
                ? undefined
                : makeRequired('Поле не может быть пустым')(item?.injunction?.dateResolved),
          }
        : undefined,
      familiarizationNotifications: getFamiliarizationNotificationsValidate({
        familiarizationNotifications: item?.familiarizationNotifications,
        signers: item?.signers,
        firstExtractDateTime: item?.firstExtractDateTime,
        isUseSigners:
          isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1),
      }),
      productsDescription:
        item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? makeRequired('Поле не может быть пустым')(item?.productsDescription)
          : undefined,
      prescriptionForUnidentifiedPersons:
        item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? makeRequired('Поле не может быть пустым')(item?.prescriptionForUnidentifiedPersons)
          : undefined,
      validTo:
        item.kindDecision?.code === KindResultDecisionCodesEnum.D_10 ? getProductSalesValidToValidate(item) : undefined,
      productSalesResumption:
        item.kindDecision?.code === KindResultDecisionCodesEnum.D_10
          ? getProductSalesResumptionValidate(item)
          : undefined,
    };
  });
};

const documentCommonRequired = (
  document: IKnmFormDocumentCommon | undefined,
): { [key: string]: string } | string | undefined => {
  if (!document) {
    // eslint-disable-next-line no-param-reassign
    document = {
      active: undefined,
      attachments: undefined,
      correlationToken: undefined,
      description: undefined,
      guid: undefined,
      type: undefined,
    };
  }

  return {
    attachments: makeRequired(REQUIRED_ERROR)(document?.attachments),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
};
